/**
 * AANKH - Microphone Panel Component
 * 
 * @author Harshit Khemani
 * @version 1.9.0
 * @description Enhanced audio streaming and recording panel with real-time audio playback, settings configuration, and reset to default
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevice } from '../hooks/useDevice';

/**
 * Animated waveform visualizer
 */
function Waveform({ analyser, isActive }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    if (!analyser || !isActive) {
      // Draw idle state
      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      
      const gradient = ctx.createLinearGradient(0, 0, canvas.offsetWidth, 0);
      gradient.addColorStop(0, '#FF5893');
      gradient.addColorStop(1, '#ff8fb3');
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, canvas.offsetHeight / 2);
      ctx.lineTo(canvas.offsetWidth, canvas.offsetHeight / 2);
      ctx.stroke();
      return;
    }

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      const gradient = ctx.createLinearGradient(0, 0, canvas.offsetWidth, 0);
      gradient.addColorStop(0, '#FF5893');
      gradient.addColorStop(0.5, '#ff6b9d');
      gradient.addColorStop(1, '#FF5893');
      
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = gradient;
      ctx.beginPath();

      const sliceWidth = canvas.offsetWidth / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.offsetHeight) / 2;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }

      ctx.lineTo(canvas.offsetWidth, canvas.offsetHeight / 2);
      ctx.stroke();
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyser, isActive]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-20 sm:h-24 rounded-xl"
      style={{ width: '100%', height: '96px' }}
    />
  );
}

/**
 * Recording timer
 */
function Timer({ isRecording }) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!isRecording) {
      setSeconds(0);
      return;
    }

    const interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isRecording]);

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return (
    <span className="font-mono text-xs sm:text-sm">
      {mins.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

/**
 * Microphone Panel Component
 */
function MicrophonePanel() {
  const {
    hasMicrophone,
    microphoneStatus,
    isRecordingMicrophone,
    microphoneRecording,
    audioStream,
    microphoneConfiguration,
    microphoneConfigurationValues,
    startMicrophone,
    stopMicrophone,
    toggleMicrophoneRecording,
    updateMicrophoneSettings,
    resetMicrophoneConfiguration,
    getMicrophoneGainNode,
    getAudioContext,
  } = useDevice();

  const [analyser, setAnalyser] = useState(null);
  const audioRef = useRef(null);
  const streamAudioRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [localConfig, setLocalConfig] = useState({});

  // Initialize local config from microphone configuration
  useEffect(() => {
    if (microphoneConfiguration) {
      setLocalConfig(microphoneConfiguration);
    }
  }, [microphoneConfiguration]);

  // Set up analyser
  useEffect(() => {
    if (microphoneStatus === 'streaming') {
      const audioContext = getAudioContext();
      const gainNode = getMicrophoneGainNode();
      
      if (audioContext && gainNode) {
        const newAnalyser = audioContext.createAnalyser();
        newAnalyser.fftSize = 512;
        gainNode.connect(newAnalyser);
        setAnalyser(newAnalyser);
      }
    } else {
      setAnalyser(null);
    }
  }, [microphoneStatus, getAudioContext, getMicrophoneGainNode]);

  // Set up real-time audio streaming
  useEffect(() => {
    if (audioStream && streamAudioRef.current) {
      streamAudioRef.current.srcObject = audioStream;
    }
  }, [audioStream]);

  const isStreaming = microphoneStatus === 'streaming';

  const handleStreamToggle = useCallback(async () => {
    if (isStreaming) {
      await stopMicrophone();
    } else {
      await startMicrophone();
    }
  }, [isStreaming, startMicrophone, stopMicrophone]);

  // Handle config change
  const handleConfigChange = useCallback((key, value) => {
    const newConfig = { ...localConfig, [key]: value };
    setLocalConfig(newConfig);
    updateMicrophoneSettings({ [key]: value });
  }, [localConfig, updateMicrophoneSettings]);

  // Handle reset
  const handleReset = useCallback(async () => {
    await resetMicrophoneConfiguration();
    // Local config will update via useEffect when microphoneConfiguration changes
  }, [resetMicrophoneConfiguration]);

  return (
    <motion.div 
      className="glass-panel rounded-3xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      whileHover={{ 
        boxShadow: '0 12px 48px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.9) inset, 0 -1px 0 rgba(0,0,0,0.04) inset'
      }}
    >
      {/* Header with glass effect */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 border-b border-white/40 backdrop-blur-sm"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset'
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
          <motion.div 
            className="flex items-center gap-3 sm:gap-4"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div 
              className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br from-primary/20 to-purple-100/80 
                flex items-center justify-center text-xl sm:text-2xl lg:text-3xl backdrop-blur-sm border border-white/60 flex-shrink-0"
              style={{
                boxShadow: '0 2px 8px rgba(255,88,147,0.15), 0 1px 0 rgba(255,255,255,0.6) inset'
              }}
              whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              🎤
            </motion.div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-base sm:text-lg lg:text-xl">Microphone</h3>
              <p className="text-xs sm:text-sm lg:text-base text-gray-500">Stream and record audio</p>
            </div>
          </motion.div>
          
          {/* Status indicator with glass */}
          <motion.div 
            className={`
              flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs font-semibold glass-button flex-shrink-0
              ${isStreaming 
                ? 'text-green-700' 
                : 'text-gray-500'}
            `}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <motion.div 
              className={`relative w-2 h-2 sm:w-2.5 sm:h-2.5`}
              animate={isStreaming ? { 
                scale: [1, 1.2, 1],
                opacity: [1, 0.6, 1]
              } : {}}
              transition={{ duration: 1.5, repeat: isStreaming ? Infinity : 0 }}
            >
              {isStreaming ? (
                <>
                  <div className="absolute inset-0 rounded-full bg-green-500" />
                  <div className="absolute inset-0 rounded-full bg-green-400 blur-md opacity-60" />
                </>
              ) : (
                <div className="absolute inset-0 rounded-full bg-gray-300" />
              )}
            </motion.div>
            {isStreaming ? 'Live' : 'Idle'}
          </motion.div>
        </div>
      </div>

      {/* Waveform with glass container */}
      <div className="p-4 sm:p-6 lg:p-8"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 100%)'
        }}
      >
        <motion.div 
          className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-2 border border-white/40"
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 1px 0 rgba(255,255,255,0.6) inset'
          }}
          whileHover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7) inset' }}
          transition={{ duration: 0.3 }}
        >
          <Waveform analyser={analyser} isActive={isStreaming} />
        </motion.div>

        {/* Real-time Audio Stream */}
        {isStreaming && audioStream && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/40"
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 1px 0 rgba(255,255,255,0.6) inset'
            }}
          >
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Live Audio</p>
            <audio
              ref={streamAudioRef}
              autoPlay
              controls
              className="w-full h-8 sm:h-10"
            />
          </motion.div>
        )}

        {/* Recording indicator with glass */}
        <AnimatePresence>
          {isRecordingMicrophone && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="mt-4 flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-full
                bg-red-50/80 backdrop-blur-sm border border-red-200/40 text-red-600"
              style={{
                boxShadow: '0 2px 8px rgba(239,68,68,0.15), 0 1px 0 rgba(255,255,255,0.6) inset'
              }}
            >
              <motion.div
                className="relative w-2.5 h-2.5 sm:w-3 sm:h-3"
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [1, 0.6, 1]
                }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <div className="absolute inset-0 rounded-full bg-red-500" />
                <div className="absolute inset-0 rounded-full bg-red-400 blur-md opacity-60" />
              </motion.div>
              <span className="font-semibold text-xs sm:text-sm">Recording</span>
              <Timer isRecording={isRecordingMicrophone} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Controls */}
        <div className="mt-6 sm:mt-8 flex items-center justify-center gap-4 sm:gap-6 lg:gap-8">
          {/* Stream button - Glass/Gradient */}
          <motion.button
            onClick={handleStreamToggle}
            disabled={!hasMicrophone}
            className={`
              flex items-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 rounded-xl font-bold text-sm sm:text-base lg:text-lg
              disabled:opacity-40 disabled:cursor-not-allowed min-h-[48px] sm:min-h-[56px]
              ${isStreaming 
                ? 'glass-button text-gray-800' 
                : 'glass-gradient-button text-white'}
            `}
            whileHover={!hasMicrophone ? {} : { scale: 1.05, y: -2 }}
            whileTap={!hasMicrophone ? {} : { scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            {isStreaming ? (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                <span className="hidden sm:inline">Stop</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" 
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                <span className="hidden sm:inline">Start Streaming</span>
                <span className="sm:hidden">Start</span>
              </>
            )}
          </motion.button>

          {/* Record button - Glass */}
          <motion.button
            onClick={toggleMicrophoneRecording}
            disabled={!hasMicrophone || !isStreaming}
            className={`
              w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center
              disabled:opacity-40 disabled:cursor-not-allowed min-w-[56px] min-h-[56px]
              ${isRecordingMicrophone 
                ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' 
                : 'glass-button text-gray-700 hover:text-red-500'}
            `}
            style={isRecordingMicrophone ? {
              boxShadow: '0 4px 16px rgba(239,68,68,0.4), 0 2px 8px rgba(239,68,68,0.3), 0 1px 0 rgba(255,255,255,0.3) inset'
            } : {}}
            whileHover={(!hasMicrophone || !isStreaming) ? {} : { scale: 1.1, rotate: isRecordingMicrophone ? 90 : 0 }}
            whileTap={(!hasMicrophone || !isStreaming) ? {} : { scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            aria-label={isRecordingMicrophone ? "Stop recording" : "Start recording"}
          >
            {isRecordingMicrophone ? (
              <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            ) : (
              <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-current" />
            )}
          </motion.button>
        </div>

        {/* Settings Toggle */}
        {microphoneConfiguration && microphoneConfigurationValues && (
          <motion.button
            onClick={() => setShowSettings(!showSettings)}
            className="w-full mt-4 sm:mt-6 py-2.5 sm:py-3 rounded-xl glass-button text-gray-700 font-medium text-sm sm:text-base"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Microphone Settings</span>
              <motion.svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                animate={{ rotate: showSettings ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </div>
          </motion.button>
        )}

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && microphoneConfiguration && microphoneConfigurationValues && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mt-4 sm:mt-6"
            >
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/40 space-y-4 sm:space-y-5">
                {Object.keys(microphoneConfigurationValues).map((key) => {
                  const values = microphoneConfigurationValues[key];
                  const currentValue = localConfig[key] || values[0];
                  return (
                    <div key={key} className="space-y-2">
                      <label className="text-sm sm:text-base font-medium text-gray-700 capitalize block">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <select
                        value={currentValue}
                        onChange={(e) => handleConfigChange(key, e.target.value)}
                        disabled={!hasMicrophone || microphoneStatus !== 'idle'}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 bg-white text-sm sm:text-base 
                          focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {values.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                <motion.button
                  onClick={handleReset}
                  disabled={!hasMicrophone || microphoneStatus !== 'idle'}
                  className="w-full py-2.5 sm:py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Reset to Default
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recording playback with glass */}
        <AnimatePresence>
          {microphoneRecording && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-4 sm:pt-6 border-t border-white/40"
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <span className="text-xs sm:text-sm font-semibold text-gray-800">Last Recording</span>
                <motion.a
                  href={microphoneRecording}
                  download={`aankh-recording-${Date.now()}.wav`}
                  className="text-xs font-semibold text-primary inline-flex items-center gap-1.5"
                  whileHover={{ scale: 1.05, x: 2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  Download
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </motion.a>
              </div>
              <div 
                className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-3 border border-white/40"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 1px 0 rgba(255,255,255,0.6) inset'
                }}
              >
                <audio
                  ref={audioRef}
                  src={microphoneRecording}
                  controls
                  className="w-full h-8 sm:h-10"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default MicrophonePanel;
